<template>
  <div class="leaflet">
    <div :id="id" :style="`height: ${height}px`"></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

export default {
  colors: {
    active: "#3273DC",
    circle: "#FF00FF",
    line: "#B71C1C",
    polygon: "#263238"
  },
  props: {
    id: {
      type: String,
      default: "map"
    },
    center: {
      type: Object,
      default: () => {
        return {
          lat: -36.858411,
          lng: 174.760959
        };
      }
    },
    zoom: {
      type: Number,
      default: 10
    },
    height: {
      type: Number,
      default: 500
    },
    markers: {
      type: Array,
      default: () => []
    },
    lines: {
      type: Array,
      default: () => []
    },
    polygons: {
      type: Array,
      default: () => []
    },
    circles: {
      type: Array,
      default: () => []
    },
    drawable: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    map: null,
    previousActive: {
      obj: null,
      colorAttr: null
    },
    me: {
      marker: null,
      radius: null
    },
    mainLayer: null,
    meLayer: null,
    drawControl: null
  }),
  mounted() {
    this.loadMap();
    this.loadMarkers();
    this.loadLines();
    this.loadPolygons();
    this.loadCircles();
  },
  watch: {
    drawable(val) {
      if (val) {
        this.map.addControl(this.drawControl);
      } else {
        this.map.removeControl(this.drawControl);
      }
    }
  },
  methods: {
    loadMap() {
      this.map = new L.Map(this.id, {
        zoom: this.zoom,
        layers: [
          new L.tileLayer(
            `https://api.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}.png?access_token=${process.env.LEAFLET_ACCESS_TOKEN}`,
            {
              attribution:
                'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
              id: "mapbox.streets",
              accessToken: process.env.LEAFLET_ACCESS_TOKEN
            }
          )
        ]
      });
      
      this.resetView();

      setTimeout(() => {
        this.resetView();
      }, 500);

      /**
       * Fix - Broken Marker Image Urls
       */
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        shadowUrl: require("leaflet/dist/images/marker-shadow.png")
      });

      /**
       * Map Layers
       */
      this.mainLayer = L.layerGroup().addTo(this.map);
      this.meLayer = L.layerGroup().addTo(this.map);

      /**
       * Map Controls
       */
      L.Control.Locate = L.Control.extend({
        onAdd: () => {
          let container = L.DomUtil.create("div", "leaflet-bar");
          let btn = L.DomUtil.create("a", "", container);
          let icon = L.DomUtil.create("i", "fa fa-map-marker fa-lg", btn);

          btn.style = "color:	#048abf";

          L.DomEvent.on(btn, "click", () => {
            this.map.locate({ setView: true });
          });

          return container;
        },
        onRemove: () => {}
      });

      L.control.locate = function(opts) {
        return new L.Control.Locate(opts);
      };

      L.control.locate({ position: "bottomleft" }).addTo(this.map);

      /**
       * Draw Control
       */
      L.drawLocal.draw.toolbar.buttons.marker = "Draw a point";
      this.drawControl = new L.Control.Draw({
        position: "topright",
        draw: {
          polyline: true,
          polygon: true,
          rectangle: false,
          circle: false,
          marker: true,
          circlemarker: false
        }
      });
      if (this.drawable) {
        this.map.addControl(this.drawControl);
      }

      /**
       * Map Events
       */
      this.map.on("click", event => {
        this.$emit("click-map", event);
      });

      this.map.on("locationfound", event => {
        if (!this.me.marker) {
          this.me.marker = this.loadObject(
            { latLng: event.latlng },
            "circleMarker",
            "circle",
            {
              radius: 10,
              weight: 5,
              color: "#FFF",
              fillColor: "#222",
              fillOpacity: 1
            },
            "fillColor",
            this.meLayer
          );
          this.me.radius = this.loadObject(
            { latLng: event.latlng },
            "circle",
            "circle",
            {
              radius: 700,
              weight: 1,
              color: "#222",
              fillColor: "#3388ff",
              fillOpacity: 0.2
            },
            "fillColor",
            this.meLayer
          );
        } else {
          this.me.marker.setLatLng(event.latlng);
          this.me.radius.setLatLng(event.latlng);
        }
      });

      this.map.on("draw:created", event => {
        this.$emit("draw-created", event);
      });
    },
    resetView() {
      this.map.setView(this.center);
      this.map.invalidateSize();
    },
    clearLayers() {
      this.mainLayer.clearLayers();
    },
    loadMarkers() {
      this.markers.forEach(marker => {
        L.marker(marker).addTo(this.mainLayer);
      });
    },
    loadLines() {
      let options = { color: this.$options.colors.line };

      this.lines.forEach(line => {
        this.loadObject(line, "polyline", "line", options);
      });
    },
    loadPolygons() {
      let options = { color: this.$options.colors.polygon };

      this.polygons.forEach(polygon => {
        this.loadObject(polygon, "polygon", "polygon", options);
      });
    },
    loadCircles() {
      let options = {
        radius: 5,
        stroke: true,
        color: "black",
        opacity: 1,
        weight: 1,
        fill: true,
        fillColor: this.$options.colors.circle,
        fillOpacity: 1
      };

      this.circles.forEach(circle => {
        this.loadObject(circle, "circle", "circle", options, "fillColor");
      });
    },
    loadObject(
      obj,
      ltype,
      type,
      options = {},
      colorAttr = "color",
      layer = this.mainLayer
    ) {
      if (obj.options) {
        options = Object.assign(options, obj.options);
      }

      let lobj = L[ltype](
        obj[`${type === "circle" ? "latLng" : "latLngs"}`],
        options
      ).addTo(layer);

      if (obj.active) {
        this.setActive(lobj, colorAttr);
      }

      if (obj.options && !obj.options.disabled) {
        lobj.on("click", event => {
          this.$emit(`click-${type}`, event);
          this.setActive(lobj, colorAttr);
        });
      }

      if (obj.hoverEvent && obj.hoverEvent.content) {
        this.bindPopup(lobj, obj.hoverEvent.content);
      }

      return lobj;
    },
    setActive(obj, colorAttr) {
      let options = {};

      if (this.previousActive.obj) {
        options[this.previousActive.colorAttr] = this.previousActive.color;
        this.previousActive.obj.setStyle(options);
      }

      this.previousActive.obj = obj;
      this.previousActive.color = obj.options.color;
      this.previousActive.colorAttr = colorAttr;

      options = {};
      options[colorAttr] = this.$options.colors.active;

      obj.setStyle(options);
    },
    bindPopup(lobj, content) {
      lobj.bindPopup(content);
      lobj.on("mouseover", () => {
        lobj.openPopup();
      });
      lobj.on("mouseout", () => {
        lobj.closePopup();
      });
    }
  }
};
</script>

<style scoped>
.leaflet-container {
  z-index: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/LeafletMap.vue